import { useCallback, useRef, useState } from 'react';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { useComponentProps } from '@optimus-web/common';
import { getDisableClickAwayProps, ScrollAwayProps, withScrollAway } from '@optimus-web/helpers';
import { Button } from '../Button';
import { ExpandIcon } from '../ExpandIcon';
import { IconButton } from '../IconButton';
import { MenuProps } from '../Menu';
import { ISelectList } from '../Select';
import { BreakpointRangeWithFallback } from '../types';
import { ensureString, getBreakpointRangeValueFallback } from '../utils';
import { useFocusTrap } from '../hooks';
import { useDropdownState, useKeyboardNavigation, useMenuItems } from './hooks';
import { ContentElement, StyledMenu } from './style';
import { DropDownButtonProps } from './types';
import { DROPDOWN_MENU_ROLE } from './hooks/useKeyboardNavigation';

interface MenuWithRef extends ISelectList {
    containerRef: HTMLDivElement | null;
}

const DropDownButtonComponent = ({
    children,
    disabled,
    label,
    size: customSize,
    variant,
    wide = false,
    portalContainerId,
    icon,
    overlayMinWidth,
    placement,
    registerScrollAway,
    deregisterScrollAway,
    footer,
    breakpointRange,
    closeOnSelect = true,
    ...other
}: DropDownButtonProps & ScrollAwayProps & { breakpointRange: BreakpointRangeWithFallback }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement & MenuWithRef>(null) as React.MutableRefObject<
        (HTMLDivElement & MenuWithRef) | null
    >;
    const [menuElement, setMenuElement] = useState<HTMLDivElement | null>(null);
    const itemsRef = useRef<HTMLDivElement[]>([]);
    const footerRef = useRef<HTMLDivElement>(null);
    const [activeItemIndex, setActiveItemIndex] = useState<number>(0);

    const isMobile = isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange));

    const { opened, open, close } = useDropdownState(buttonRef, menuRef, registerScrollAway, deregisterScrollAway);

    useFocusTrap(opened && menuElement ? [buttonRef.current, menuElement] : null);

    const { getFirstItem, handleMenuButtonKeyDown, handleMenuKeyDown } = useKeyboardNavigation(
        itemsRef,
        opened,
        open,
        close,
        footerRef,
        setActiveItemIndex
    );

    const { size } = useComponentProps('Button', { size: customSize });
    const renderAsIconButton = icon && !label;

    const handleButtonClick = useCallback(
        (event: React.SyntheticEvent) => {
            event.stopPropagation();
            event.preventDefault();

            if (!opened) {
                open({ getFocusItem: getFirstItem });
            } else {
                close();
            }
        },
        [opened, open, close, getFirstItem]
    );

    const menuItems = useMenuItems({
        children,
        itemsRef,
        close: closeOnSelect ? close : undefined,
        activeItemIndex,
    });

    const getOverlayMinWidth = useCallback(() => {
        if (overlayMinWidth) {
            return overlayMinWidth;
        }

        const DEFAULT_DROPDOWN_BUTTON_MIN_WIDTH = 160;
        return renderAsIconButton ? undefined : DEFAULT_DROPDOWN_BUTTON_MIN_WIDTH;
    }, [overlayMinWidth, renderAsIconButton]);

    // Add ref forwarding to get access to the menu's container, needed for detecting scroll
    const handleMenuRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            const menuContainer = node.querySelector(`[role="${DROPDOWN_MENU_ROLE}"]`);

            // Node with the additional properties
            const enhancedNode = Object.assign(node, {
                containerRef: (menuContainer as HTMLDivElement) || node,
                updateMaxHeight: () => {}, // satisfy ISelectList interface
            });

            menuRef.current = enhancedNode;
            setMenuElement(enhancedNode);
        } else {
            menuRef.current = null;
            setMenuElement(null);
        }
    }, []);

    const buttonProps = {
        'aria-haspopup': true,
        'aria-expanded': opened,
        disabled,
        onClick: handleButtonClick,
        size,
        variant,
        wide,
        onKeyDown: handleMenuButtonKeyDown,
        ref: buttonRef,
        icon,
        'aria-label': ensureString({ value: label }),
        ...(opened ? getDisableClickAwayProps() : {}),
        ...other,
    };

    const menuProps = {
        ...(other as unknown as MenuProps),
        portalContainerId,
        anchorElement: buttonRef.current,
        sizeToAnchor: wide,
        opened,
        onKeyDown: handleMenuKeyDown,
        role: DROPDOWN_MENU_ROLE,
        overlayMinWidth: getOverlayMinWidth(),
        ref: handleMenuRef,
        placement,
        onItemClick: closeOnSelect ? close : undefined,
        footerProps: {
            primary: footer?.primary,
            secondary: footer?.secondary,
            footerRef,
            close,
            listRef: menuRef,
            isMobile,
        },
    };

    return (
        <>
            {renderAsIconButton ? (
                <IconButton type="button" {...buttonProps} icon={icon} />
            ) : (
                <Button type="button" {...buttonProps}>
                    <ContentElement>
                        <div>{label}</div>
                        <ExpandIcon disabled={disabled} expanded={opened} size={size} />
                    </ContentElement>
                </Button>
            )}
            <StyledMenu {...menuProps}>{menuItems}</StyledMenu>
        </>
    );
};

export const DropDownButton = withScrollAway(withBreakpointRange(DropDownButtonComponent));
